/**
 * One site's page: the row, 90 days of rollups, the confirmed incidents and the
 * last day of checks.
 *
 * Polled once a minute while the tab is visible and the site is being checked,
 * the same cadence as the desk. A 404 is its own state rather than a failure:
 * the site was removed (in another tab, by a colleague), and the page should
 * say so and offer the way back instead of a retry button that can never work.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../lib/api";
import { getMonitor, type MonitorDetailResponse, type MonitorRow } from "../../../lib/monitoring";
import { MONITOR_POLL_MS, useVisiblePoll } from "./useMonitors";

export function useMonitor(id: string) {
  const [data, setData] = useState<MonitorDetailResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const rid = ++requestId.current;
    try {
      const res = await getMonitor(id);
      if (!alive.current || rid !== requestId.current) return;
      setData(res);
      setFailed(false);
      setNotFound(false);
    } catch (err) {
      if (!alive.current || rid !== requestId.current) return;
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else setFailed(true);
    }
  }, [id]);

  useEffect(() => {
    // A different site: the old one's numbers must not stand under the new name
    // while the first read is out.
    setData(null);
    setFailed(false);
    setNotFound(false);
    void load();
  }, [load]);

  useVisiblePoll(Boolean(data && !data.site.paused), MONITOR_POLL_MS, () => void load());

  /** The row an action answered with, in place, then a full read for the rest
   *  (a check-now adds a check to the chart and may open an incident). */
  const replaceSite = useCallback(
    (site: MonitorRow) => {
      setData((d) => (d ? { ...d, site } : d));
      void load();
    },
    [load],
  );

  return { data, failed, notFound, reload: load, replaceSite };
}
